import React, { useState, useEffect, useMemo, useCallback, useLayoutEffect, useRef } from "react";
import { View, StyleSheet, TextInput, Pressable, Platform, ScrollView as RNScrollView } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { useToolTip } from "@/hooks/useToolTip";
import { useModal } from "@/hooks/useModal";
import { ConfirmModal } from "@/components/Modals/ConfirmModal";
import useDateTime from "@/hooks/useDateTime";
import useAuth from "@/hooks/useAuth";
import usePermissions from "@/hooks/usePermissions";
import { useCurrentEntity } from "@/contexts/CurrentEntityContext";
import { useNavigationHistory } from "@/contexts/NavigationHistoryContext";
import { shiftsService } from "@/services/shiftService";
import { ShiftType } from "@/types/db/shifts";
import { ShiftUserStatusType } from "@/types/db/shift_users";
import { ShiftPositionType } from "@/types/db/shift_positions";
import useNavigationGuard from "@/hooks/useNavigationGuard";
import { useNavigation } from "@react-navigation/native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import {
  Table,
  ColumnDef,
  EditableOption,
  ProgressCell,
  calculateProgress,
  DurationCell,
  calculateDuration,
} from "@/components/NewTable";
import { SmallToggle } from "@/components/SmallToggle";
import { theme } from "@/constants/themes";
import { ScrollView } from "react-native-gesture-handler";
import { ShiftTimeInput } from "@/components/experimental/ShiftTimeInput";

// Status color mapping for shifts
const STATUS_COLORS: Record<string, keyof Pick<theme, "complete" | "pending" | "attention" | "neutral" | "todo">> = {
  PENDING: "pending",
  APPROVED: "complete",
  ADVANCED: "complete",
  CANCELED: "attention",
  "CANCELED - BILLABLE": "neutral",
  COMPLETE: "todo",
};

// Status color mapping for shift_users
const SHIFT_USER_STATUS_COLORS: Record<
  string,
  keyof Pick<theme, "complete" | "pending" | "attention" | "neutral" | "todo">
> = {
  QUEUED: "neutral",
  INVITED: "pending",
  ACCEPTED: "complete",
  DECLINED: "attention",
  CANCELLED: "attention",
  "NOT NEEDED": "neutral",
  "NO RESPONSE": "attention",
  "NO SHOW": "attention",
  "ATTENDANCE EXCEPTION": "attention",
  COMPLETE: "todo",
};

// Tooltip content for positions breakdown
function PositionsTooltipContent({ positions, colors }: { positions: ShiftPositionType[]; colors: theme }) {
  if (!positions.length) return null;

  return (
    <View style={[tooltipStyles.container, { backgroundColor: colors.background, borderColor: colors.border }]}>
      <ThemedText style={tooltipStyles.header}>Positions</ThemedText>
      <View style={[tooltipStyles.divider, { backgroundColor: colors.border }]} />
      <View style={tooltipStyles.positionsList}>
        {positions.map((pos, idx) => {
          const needed = pos.quantity || 0;
          const preferred = (pos as any).preferred_quantity ?? needed;
          const assigned = pos.shift_users?.length || 0;

          // Determine status color based on fill level
          let statusColor = colors.attention.border; // red - nothing assigned
          if (assigned > 0 && assigned < needed) {
            statusColor = colors.pending.border; // yellow - some but not enough
          } else if (assigned >= needed && assigned < preferred) {
            statusColor = colors.todo.border; // blue - met needed but not preferred
          } else if (assigned >= preferred) {
            statusColor = colors.complete.border; // green - met preferred
          }

          return (
            <View key={pos.id || idx} style={tooltipStyles.positionRow}>
              <View style={[tooltipStyles.statusDot, { backgroundColor: statusColor }]} />
              <ThemedText style={tooltipStyles.positionName} numberOfLines={1}>
                {pos.position?.name || "Unknown"}
              </ThemedText>
              <ThemedText style={[tooltipStyles.positionCount, { color: colors.textSecondary }]}>
                {assigned}/{needed}
                {preferred > needed && <ThemedText style={{ color: colors.textSecondary }}> ({preferred})</ThemedText>}
              </ThemedText>
            </View>
          );
        })}
      </View>
    </View>
  );
}

// Cell component for positions count with tooltip
function PositionsCell({
  positions,
  colors,
  isEditing,
}: {
  positions: ShiftPositionType[];
  colors: theme;
  isEditing?: boolean;
}) {
  const toolTip = useToolTip();
  const ref = useRef<View>(null);
  const isHovering = useRef(false);

  const handlePointerEnter = useCallback(() => {
    // Don't show tooltip while editing
    if (!positions.length || isEditing) return;
    isHovering.current = true;

    ref.current?.measureInWindow((x, y, width) => {
      if (!isHovering.current) return;
      toolTip.setLocation({
        width: 220,
        height: positions.length * 28 + 60,
        x: x + width + 4,
        y: y - 10,
      });
      toolTip.setToolTipContent(<PositionsTooltipContent positions={positions} colors={colors} />);
      toolTip.setVisible(true);
    });
  }, [positions, colors, toolTip, isEditing]);

  const handlePointerLeave = useCallback(() => {
    isHovering.current = false;
    toolTip.setVisible(false);
  }, [toolTip]);

  if (!positions.length) {
    return <ThemedText style={styles.countText}>0</ThemedText>;
  }

  return (
    <View
      ref={ref}
      onPointerEnter={isEditing ? undefined : handlePointerEnter}
      onPointerLeave={isEditing ? undefined : handlePointerLeave}
    >
      <ThemedText
        style={[styles.countText, !isEditing && { textDecorationLine: "underline", textDecorationStyle: "dotted" }]}
      >
        {positions.length}
      </ThemedText>
    </View>
  );
}

const tooltipStyles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderRadius: 10,
    padding: 10,
    gap: 6,
    minWidth: 180,
    maxWidth: 260,
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.15)",
  },
  header: {
    fontSize: 12,
    fontWeight: "700",
  },
  divider: {
    height: StyleSheet.hairlineWidth,
  },
  positionsList: {
    gap: 6,
  },
  positionRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  positionName: {
    flex: 1,
    fontSize: 12,
  },
  positionCount: {
    fontSize: 11,
    fontWeight: "600",
  },
});

// Header button component with tooltip support
interface HeaderButtonProps {
  icon: keyof typeof MaterialCommunityIcons.glyphMap;
  tooltip: string;
  onPress: () => void;
  disabled?: boolean;
}

function HeaderButton({ icon, tooltip, onPress, disabled }: HeaderButtonProps) {
  const { colors } = useTheme();
  const toolTip = useToolTip();
  const ref = useRef<View>(null);
  const isHovering = useRef(false);

  const handlePointerEnter = useCallback(() => {
    isHovering.current = true;
    toolTip.setToolTipContent(
      <View
        style={{
          backgroundColor: colors.background,
          paddingHorizontal: 8,
          paddingVertical: 4,
          borderRadius: 8,
          borderWidth: StyleSheet.hairlineWidth,
          borderColor: colors.border,
        }}
      >
        <ThemedText style={{ fontSize: 12 }}>{tooltip}</ThemedText>
      </View>,
    );
    ref.current?.measureInWindow((x: number, y: number, w: number) => {
      if (!isHovering.current) return;
      // Position above the button, right-aligned (tooltip extends left from button)
      toolTip.setLocation({ x: x + (w || 0) - 180, y: y - 38, width: 180, height: 30 } as any);
      toolTip.setVisible(true);
    });
  }, [tooltip, colors.background, colors.border, toolTip]);

  const handlePointerLeave = useCallback(() => {
    isHovering.current = false;
    toolTip.setVisible(false);
  }, [toolTip]);

  const handlePress = useCallback(() => {
    if (disabled) return;
    isHovering.current = false;
    toolTip.setVisible(false);
    onPress();
  }, [onPress, toolTip, disabled]);

  return (
    <Pressable
      ref={ref}
      style={{ marginRight: 16, padding: 4, opacity: disabled ? 0.4 : 1 }}
      onPress={handlePress}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
    >
      <MaterialCommunityIcons name={icon} size={24} color={disabled ? colors.textSecondary : colors.primary} />
    </Pressable>
  );
}

// Enriched shift type with flattened shift_user data for the current user
type EnrichedShiftType = ShiftType & {
  shift_user_id?: string;
  shift_user_status?: ShiftUserStatusType;
  shift_position_id?: string;
  position_name?: string;
};

export interface RelatedShiftsScreenProps {
  showInactive?: boolean;
  userId?: string;
  /** Callback when showInactive toggle changes (only shown for user panel) */
  onShowInactiveChange?: (value: boolean) => void;
}

export function RelatedShiftsScreen({ showInactive = false, userId, onShowInactiveChange }: RelatedShiftsScreenProps) {
  const { colors } = useTheme();
  const { formatTimestamp, toUserTimezone } = useDateTime();
  const { displayNames } = useAuth();
  const { shifts: shiftPermissions, shift_users: shiftUserPermissions } = usePermissions();
  const currentEntity = useCurrentEntity();
  const { pushNavigation } = useNavigationHistory();
  const navigation = useNavigation();
  const { openModal, closeModal } = useModal();
  const { navigateToEntity } = useNavigationGuard();
  const [shifts, setShifts] = useState<ShiftType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [showAddDrawer, setShowAddDrawer] = useState(false);

  // New row form values for add drawer
  const [newRowValues, setNewRowValues] = useState<Partial<EnrichedShiftType>>({
    name: "",
    status: "PENDING",
    start_time: undefined,
    end_time: undefined,
  });

  useEffect(() => {
    const fetchShifts = async () => {
      if (!currentEntity?.currentId || !currentEntity?.currentPanel) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        let fetchedShifts: ShiftType[] = [];

        switch (currentEntity.currentPanel) {
          case "project":
            fetchedShifts = await shiftsService.getShiftsByProjectId(currentEntity.currentId);
            break;
          case "user":
            fetchedShifts = await shiftsService.getShiftsByUserId(userId || currentEntity.currentId, false);
            break;
          case "client":
            fetchedShifts = await shiftsService.getShiftsByClientId(currentEntity.currentId);
            break;
          default:
            console.warn(`No shift relationship defined for entity type: ${currentEntity.currentPanel}`);
        }

        setShifts(fetchedShifts);
      } catch (err: any) {
        console.error("Failed to fetch related shifts:", err);
        setError(err.message || "Failed to load shifts");
      } finally {
        setLoading(false);
      }
    };

    fetchShifts();
  }, [currentEntity?.currentId, currentEntity?.currentPanel, userId]);

  // Filter and sort shifts based on active/inactive toggle
  const filteredShifts = useMemo(() => {
    const now = new Date();

    const sortedShifts = [...shifts].sort((a, b) => {
      const aTime = a.end_time ? toUserTimezone(a.end_time).getTime() : 0;
      const bTime = b.end_time ? toUserTimezone(b.end_time).getTime() : 0;
      return bTime - aTime;
    });

    if (!showInactive) {
      return sortedShifts.filter((shift) => {
        if (!shift.end_time) return false;
        const shiftEndTime = toUserTimezone(shift.end_time);
        return shiftEndTime >= now;
      });
    }

    return sortedShifts;
  }, [shifts, showInactive, toUserTimezone]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getStatusColor = (status?: string) => {
    const colorKey = STATUS_COLORS[status || "PENDING"] || "pending";
    return colors[colorKey].border;
  };

  // Status options for shift editable picker
  const statusOptions: EditableOption[] = useMemo(
    () => [
      { value: "PENDING", label: "Pending", color: colors.pending.border },
      { value: "APPROVED", label: "Approved", color: colors.complete.border },
      { value: "ADVANCED", label: "Advanced", color: colors.complete.border },
      { value: "CANCELED", label: "Canceled", color: colors.attention.border },
      { value: "CANCELED - BILLABLE", label: "Canceled - Billable", color: colors.neutral.border },
      { value: "COMPLETE", label: "Complete", color: colors.todo.border },
    ],
    [colors],
  );

  // Status options for shift_users editable picker
  const shiftUserStatusOptions: EditableOption[] = useMemo(
    () => [
      { value: "QUEUED", label: "Queued", color: colors.neutral.border },
      { value: "INVITED", label: "Invited", color: colors.pending.border },
      { value: "ACCEPTED", label: "Accepted", color: colors.complete.border },
      { value: "DECLINED", label: "Declined", color: colors.attention.border },
      { value: "CANCELLED", label: "Cancelled", color: colors.attention.border },
      { value: "NOT NEEDED", label: "Not Needed", color: colors.neutral.border },
      { value: "NO RESPONSE", label: "No Response", color: colors.attention.border },
      { value: "NO SHOW", label: "No Show", color: colors.attention.border },
      { value: "ATTENDANCE EXCEPTION", label: "Attendance Exception", color: colors.attention.border },
      { value: "COMPLETE", label: "Complete", color: colors.todo.border },
    ],
    [colors],
  );

  // Enrich shifts with current user's shift_user data when viewing user panel
  const enrichedShifts: EnrichedShiftType[] = useMemo(() => {
    // Only enrich when viewing user panel
    if (currentEntity?.currentPanel !== "user") {
      return filteredShifts;
    }

    const targetUserId = userId || currentEntity?.currentId;
    if (!targetUserId) {
      return filteredShifts;
    }

    return filteredShifts.map((shift) => {
      // Find the shift_user record for the current user
      let userShiftData: Partial<EnrichedShiftType> = {};

      // Search through shift_positions to find this user's assignment
      for (const position of shift.shift_positions || []) {
        const shiftUser = position.shift_users?.find((su) => su.id === targetUserId);
        if (shiftUser) {
          userShiftData = {
            shift_user_id: shiftUser.id,
            shift_user_status: shiftUser.status as ShiftUserStatusType,
            shift_position_id: position.id,
            position_name: position.position?.name || "Unknown Position",
          };
          break;
        }
      }

      return {
        ...shift,
        ...userShiftData,
      };
    });
  }, [filteredShifts, currentEntity?.currentPanel, currentEntity?.currentId, userId]);

  // Count queued shifts for invite all action (only when viewing user panel)
  const queuedShifts = useMemo(() => {
    if (currentEntity?.currentPanel !== "user") return [];
    return enrichedShifts.filter((s) => s.shift_user_status === "QUEUED");
  }, [enrichedShifts, currentEntity?.currentPanel]);
  const queuedCount = queuedShifts.length;

  // Handle invite all queued shifts
  const handleInviteAll = useCallback(() => {
    if (queuedCount === 0) return;

    openModal(
      <ConfirmModal
        title="Invite All Queued Shifts"
        message={`You are about to invite this user to ${queuedCount} shift${queuedCount !== 1 ? "s" : ""}. They will receive notifications about these shifts.`}
        confirmText="Invite All"
        cancelText="Cancel"
        onConfirm={async () => {
          closeModal();
          try {
            // Update each queued shift's status to INVITED
            await Promise.all(
              queuedShifts.map((shift) => {
                if (shift.shift_position_id && shift.shift_user_id) {
                  return shiftsService.updateShiftUserStatus(shift.shift_position_id, shift.shift_user_id, "INVITED");
                }
                return Promise.resolve();
              }),
            );

            // Update local state for all queued shifts
            setShifts((prev) =>
              prev.map((shift) => {
                const queuedShift = queuedShifts.find((q) => q.id === shift.id);
                if (!queuedShift) return shift;

                return {
                  ...shift,
                  shift_positions: shift.shift_positions?.map((pos) => {
                    if (pos.id !== queuedShift.shift_position_id) return pos;
                    return {
                      ...pos,
                      shift_users: pos.shift_users?.map((su) =>
                        su.id === queuedShift.shift_user_id ? { ...su, status: "INVITED" } : su,
                      ),
                    };
                  }),
                };
              }),
            );
          } catch (error) {
            console.error("Failed to invite users:", error);
          }
        }}
        onCancel={closeModal}
      />,
    );
  }, [queuedCount, queuedShifts, openModal, closeModal]);

  // Set up header with search and invite all button
  useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: () => (
        <View style={styles.headerTitleContainer}>
          <ThemedText style={styles.headerTitle}>{displayNames?.shifts?.plural || "Shifts"}</ThemedText>
          <View style={[styles.headerSearchContainer, { backgroundColor: colors.backgroundSecondary }]}>
            <MaterialCommunityIcons
              name="magnify"
              size={16}
              color={colors.textSecondary}
              style={styles.headerSearchIcon}
            />
            <TextInput
              value={searchValue}
              onChangeText={setSearchValue}
              placeholder="Search..."
              placeholderTextColor={colors.textSecondary}
              style={[styles.headerSearchInput, { color: colors.text }]}
              autoCapitalize="none"
              autoCorrect={false}
            />
            {searchValue.length > 0 && (
              <Pressable onPress={() => setSearchValue("")} style={styles.headerClearButton}>
                <MaterialCommunityIcons name="close-circle" size={14} color={colors.textSecondary} />
              </Pressable>
            )}
          </View>
        </View>
      ),
      headerRight: () => (
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8, marginRight: 8 }}>
          {currentEntity?.currentPanel === "project" && shiftPermissions.create && (
            <HeaderButton
              icon="plus"
              tooltip={`Add ${displayNames?.shifts?.name || "Shift"}`}
              onPress={() => setShowAddDrawer(true)}
            />
          )}
          {currentEntity?.currentPanel === "user" && (
            <>
              <HeaderButton
                icon="send"
                tooltip={
                  !shiftUserPermissions.update
                    ? `You don't have permission to invite ${displayNames?.users?.plural?.toLowerCase() || "users"}`
                    : queuedCount === 0
                      ? `No queued ${displayNames?.shifts?.plural?.toLowerCase() || "shifts"} to invite`
                      : `Invite to ${queuedCount} queued ${queuedCount !== 1 ? displayNames?.shifts?.plural?.toLowerCase() || "shifts" : displayNames?.shifts?.name?.toLowerCase() || "shift"}`
                }
                onPress={handleInviteAll}
                disabled={!shiftUserPermissions.update || queuedCount === 0}
              />
              {onShowInactiveChange && (
                <SmallToggle value={showInactive} onValueChange={onShowInactiveChange} label="Show Completed" />
              )}
            </>
          )}
        </View>
      ),
    });
  }, [
    navigation,
    searchValue,
    colors,
    displayNames,
    currentEntity?.currentPanel,
    shiftPermissions.create,
    shiftUserPermissions.update,
    queuedCount,
    handleInviteAll,
    onShowInactiveChange,
    showInactive,
  ]);

  // Handle edit callback for shift fields
  const handleEdit = useCallback(async (row: EnrichedShiftType, key: string, newValue: string) => {
    if (!row.id) return;

    try {
      // Update the shift in the database
      await shiftsService.updateShift(row.id, { [key]: newValue });

      // Update local state
      setShifts((prev) => prev.map((shift) => (shift.id === row.id ? { ...shift, [key]: newValue } : shift)));
    } catch (err) {
      console.error("Failed to update shift:", err);
    }
  }, []);

  // Handle edit callback for shift_users.status (column-level)
  const handleShiftUserStatusEdit = useCallback(async (row: EnrichedShiftType, _key: string, newValue: string) => {
    if (!row.shift_position_id || !row.shift_user_id) {
      console.error("Missing shift_position_id or shift_user_id for status update");
      return;
    }

    try {
      await shiftsService.updateShiftUserStatus(
        row.shift_position_id,
        row.shift_user_id,
        newValue as ShiftUserStatusType,
      );

      // Update local state - update the nested shift_user status
      setShifts((prev) =>
        prev.map((shift) => {
          if (shift.id !== row.id) return shift;

          return {
            ...shift,
            shift_positions: shift.shift_positions?.map((pos) => {
              if (pos.id !== row.shift_position_id) return pos;
              return {
                ...pos,
                shift_users: pos.shift_users?.map((su) =>
                  su.id === row.shift_user_id ? { ...su, status: newValue } : su,
                ),
              };
            }),
          };
        }),
      );
    } catch (err) {
      console.error("Failed to update shift user status:", err);
    }
  }, []);

  // Handle save new shift
  const handleSaveShift = useCallback(async () => {
    if (!currentEntity?.currentId || !newRowValues.name) return;
    try {
      // Convert dates to ISO strings if needed (row values may be Date | string | null)
      const st = newRowValues.start_time as Date | string | null;
      const startTime = st instanceof Date ? st.toISOString() : st || null;
      const et = newRowValues.end_time as Date | string | null;
      const endTime = et instanceof Date ? et.toISOString() : et || null;

      const newShift = await shiftsService.createShift({
        project_id: currentEntity.currentId,
        name: newRowValues.name,
        status: newRowValues.status || "PENDING",
        start_time: startTime,
        end_time: endTime,
      });
      // Add to local state
      setShifts((prev) => [newShift, ...prev]);
      // Reset form
      setNewRowValues({
        name: "",
        status: "PENDING",
        start_time: undefined,
        end_time: undefined,
      });
      setShowAddDrawer(false);
    } catch (err) {
      console.error("Failed to create shift:", err);
    }
  }, [currentEntity?.currentId, newRowValues]);

  // Handle delete callback
  const handleDelete = useCallback(async (row: EnrichedShiftType) => {
    if (!row.id) return;

    try {
      // Delete the shift from the database
      await shiftsService.deleteShift(row.id);

      // Update local state
      setShifts((prev) => prev.filter((shift) => shift.id !== row.id));
    } catch (err) {
      console.error("Failed to delete shift:", err);
    }
  }, []);

  const handleShiftPress = useCallback(
    (shift: EnrichedShiftType) => {
      if (currentEntity?.currentPanel && currentEntity?.currentId) {
        pushNavigation({
          panel: currentEntity.currentPanel,
          id: currentEntity.currentId,
          title: currentEntity.currentTitle || currentEntity.currentPanel,
        });
      }
      navigateToEntity("shift", shift.id!);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentEntity, pushNavigation],
  );

  const handleProjectPress = useCallback(
    (shift: EnrichedShiftType) => {
      if (!shift.project?.id) return;
      if (currentEntity?.currentPanel && currentEntity?.currentId) {
        pushNavigation({
          panel: currentEntity.currentPanel,
          id: currentEntity.currentId,
          title: currentEntity.currentTitle || currentEntity.currentPanel,
        });
      }
      navigateToEntity("project", shift.project.id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentEntity, pushNavigation],
  );

  // Helper to get shift_user status color
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getShiftUserStatusColor = (status?: string) => {
    const colorKey = SHIFT_USER_STATUS_COLORS[status || "QUEUED"] || "neutral";
    return colors[colorKey].border;
  };

  // Define columns for the table
  const columns: ColumnDef<EnrichedShiftType>[] = useMemo(() => {
    const baseColumns: ColumnDef<EnrichedShiftType>[] = [
      {
        key: "name",
        header: "Name",
        flex: 1,
        minWidth: 120,
        accessor: (row) => row.name || row.description || "Unnamed Shift",
        onPress: (row) => handleShiftPress(row),
        // Only allow editing name from project panel (not user panel)
        editable:
          currentEntity?.currentPanel !== "user"
            ? {
                type: "text",
                placeholder: "Shift name",
              }
            : undefined,
      },
      {
        key: "status",
        header: `${displayNames?.shifts?.name || "Shift"} Status`,
        width: 210,
        align: "center",
        searchable: true,
        searchValue: (_, row) => row.status || "PENDING",
        // Only allow editing shift status from project panel (not user panel)
        editable:
          currentEntity?.currentPanel !== "user"
            ? {
                type: "picker",
                options: statusOptions,
                placeholder: "Select status",
              }
            : undefined,
        render: (value, row) => (
          <View style={[styles.statusPill, { backgroundColor: getStatusColor(row.status) }]}>
            <ThemedText style={styles.statusText}>{row.status || "PENDING"}</ThemedText>
          </View>
        ),
      },
    ];

    // Add positions and users count columns when viewing from project panel
    if (currentEntity?.currentPanel === "project") {
      baseColumns.push(
        {
          key: "positions_count",
          header: "Positions",
          width: 120,
          align: "center",
          sortable: true,
          searchable: false,
          accessor: (row) => row.shift_positions?.length || 0,
          render: (_, row, _idx, isEditing) => (
            <PositionsCell positions={row.shift_positions || []} colors={colors} isEditing={isEditing} />
          ),
        },
        {
          key: "users_count",
          header: `${displayNames?.users?.plural || "Users"}`,
          width: 80,
          align: "center",
          sortable: true,
          searchable: false,
          accessor: (row) => row.shift_positions?.reduce((sum, pos) => sum + (pos.shift_users?.length || 0), 0) || 0,
          render: (value) => <ThemedText style={styles.countText}>{value}</ThemedText>,
        },
      );
    }

    // Add position and user status columns when viewing user panel
    if (currentEntity?.currentPanel === "user") {
      baseColumns.push(
        {
          key: "position_name",
          header: "Position",
          width: 120,
          accessor: (row) => row.position_name || "—",
        },
        {
          key: "shift_user_status",
          header: `${displayNames?.users?.name || "User"} Status`,
          width: 140,
          align: "center",
          searchable: true,
          searchValue: (_, row) => row.shift_user_status || "",
          editable: {
            type: "picker",
            options: shiftUserStatusOptions,
            placeholder: "Select status",
            onEdit: handleShiftUserStatusEdit,
          },
          render: (value, row) => {
            if (!row.shift_user_status) return <ThemedText>—</ThemedText>;
            return (
              <View style={[styles.statusPill, { backgroundColor: getShiftUserStatusColor(row.shift_user_status) }]}>
                <ThemedText style={styles.statusText}>{row.shift_user_status}</ThemedText>
              </View>
            );
          },
        },
      );
    }

    // Add project column (skip when viewing from project panel - redundant)
    if (currentEntity?.currentPanel !== "project") {
      baseColumns.push({
        key: "project",
        header: displayNames?.projects?.name || "Project",
        flex: 1,
        minWidth: 120,
        accessor: (row) => row.project?.name || "—",
        onPress: (row) => handleProjectPress(row),
      });
    }

    // Add time columns
    baseColumns.push(
      {
        key: "progress",
        header: "Progress",
        width: 120,
        align: "center",
        searchable: false,
        accessor: (row) => calculateProgress(row.start_time, row.end_time),
        render: (value, row) => <ProgressCell startTime={row.start_time} endTime={row.end_time} />,
      },
      {
        key: "start_time",
        header: "Start",
        width: 160,
        searchable: false,
        accessor: (row) => row.start_time,
        render: (value) => <ThemedText style={styles.dateText}>{value ? formatTimestamp(value) : "—"}</ThemedText>,
      },
      {
        key: "end_time",
        header: "End",
        width: 160,
        searchable: false,
        accessor: (row) => row.end_time,
        render: (value) => <ThemedText style={styles.dateText}>{value ? formatTimestamp(value) : "—"}</ThemedText>,
      },
      {
        key: "duration",
        header: "Duration",
        width: 100,
        align: "right",
        searchable: false,
        accessor: (row) => calculateDuration(row.start_time, row.end_time),
        render: (value) => <DurationCell hours={value} />,
      },
    );

    return baseColumns;
  }, [
    colors,
    formatTimestamp,
    getStatusColor,
    getShiftUserStatusColor,
    statusOptions,
    shiftUserStatusOptions,
    currentEntity?.currentPanel,
    handleShiftUserStatusEdit,
    handleShiftPress,
    handleProjectPress,
    displayNames,
  ]);

  if (error) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={styles.emptyState}>
          <MaterialCommunityIcons name="alert-circle" size={48} color={colors.red.text} />
          <ThemedText style={styles.emptyText}>Failed to load shifts</ThemedText>
          <ThemedText style={{ opacity: 0.6, fontSize: 12 }}>{error}</ThemedText>
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <ScrollView
        horizontal={true}
        showsHorizontalScrollIndicator={true}
        contentContainerStyle={{ width: "auto", flexGrow: 1 }}
        style={{ flex: 1 }}
      >
        <Table<EnrichedShiftType>
          data={enrichedShifts}
          columns={columns}
          keyExtractor={(item) => item.id || ""}
          loading={loading}
          loadingRowCount={8}
          onEdit={handleEdit}
          canEdit={(row) => shiftPermissions.update && calculateProgress(row.start_time, row.end_time) < 100}
          onDelete={handleDelete}
          canDelete={(row) =>
            shiftPermissions.delete &&
            currentEntity?.currentPanel === "project" &&
            calculateProgress(row.start_time, row.end_time) < 100
          }
          searchValue={searchValue}
          sortable
          defaultSort={{ key: "end_time", direction: "desc" }}
          emptyMessage="No shifts found"
          emptyIcon="calendar-blank"
        />
      </ScrollView>

      {/* Add Shift Drawer */}
      {showAddDrawer && (
        <View style={[styles.drawer, { backgroundColor: colors.background, borderLeftColor: colors.border }]}>
          <View style={[styles.drawerHeader, { borderBottomColor: colors.border }]}>
            <ThemedText type="subtitle">Add {displayNames?.shifts?.name || "Shift"}</ThemedText>
            <Pressable onPress={() => setShowAddDrawer(false)}>
              <MaterialCommunityIcons name="close" size={24} color={colors.text} />
            </Pressable>
          </View>

          <RNScrollView style={styles.drawerContent}>
            {/* Name */}
            <View style={styles.formField}>
              <ThemedText style={styles.fieldLabel}>Name *</ThemedText>
              <TextInput
                style={[styles.formInput, { borderColor: colors.border, color: colors.text }]}
                value={newRowValues.name || ""}
                onChangeText={(text) => setNewRowValues((prev) => ({ ...prev, name: text }))}
                placeholder="Enter shift name"
                placeholderTextColor={colors.textSecondary}
              />
            </View>

            {/* Status */}
            <View style={styles.formField}>
              <ThemedText style={styles.fieldLabel}>Status</ThemedText>
              <Pressable style={[styles.formPicker, { borderColor: colors.border }]}>
                <View style={[styles.statusPill, { backgroundColor: getStatusColor(newRowValues.status) }]}>
                  <ThemedText style={styles.statusText}>{newRowValues.status || "PENDING"}</ThemedText>
                </View>
                <MaterialCommunityIcons name="chevron-down" size={16} color={colors.textSecondary} />
              </Pressable>
            </View>

            {/* Schedule (Start & End Time) */}
            <View style={{ marginTop: 8 }}>
              <ShiftTimeInput
                label="Schedule"
                startTime={newRowValues.start_time ? String(newRowValues.start_time) : null}
                endTime={newRowValues.end_time ? String(newRowValues.end_time) : null}
                editing={true}
                onChange={(values) => {
                  setNewRowValues((prev) => ({
                    ...prev,
                    start_time: values.startTime,
                    end_time: values.endTime,
                  }));
                }}
              />
            </View>
          </RNScrollView>

          {/* Footer Actions */}
          <View style={[styles.drawerFooter, { borderTopColor: colors.border }]}>
            <Pressable
              onPress={() => setShowAddDrawer(false)}
              style={[styles.drawerButton, { backgroundColor: colors.backgroundSecondary }]}
            >
              <ThemedText>Cancel</ThemedText>
            </Pressable>
            <Pressable
              onPress={handleSaveShift}
              disabled={!newRowValues.name}
              style={[
                styles.drawerButton,
                { backgroundColor: newRowValues.name ? colors.primary : colors.backgroundSecondary },
              ]}
            >
              <ThemedText style={{ color: newRowValues.name ? "white" : colors.textSecondary }}>Save</ThemedText>
            </Pressable>
          </View>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 8,
  },
  // Drawer styles
  drawer: {
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    width: 400,
    borderLeftWidth: 1,
    zIndex: 100,
  },
  drawerHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  drawerContent: {
    flex: 1,
    padding: 16,
  },
  formField: {
    marginBottom: 16,
  },
  fieldLabel: {
    fontSize: 12,
    marginBottom: 4,
    opacity: 0.7,
  },
  formInput: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
  },
  formPicker: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  drawerFooter: {
    flexDirection: "row",
    gap: 12,
    padding: 16,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  drawerButton: {
    flex: 1,
    alignItems: "center",
    paddingVertical: 12,
    borderRadius: 8,
  },
  // Header styles
  headerTitleContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: "600",
  },
  headerSearchContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
    minWidth: 140,
  },
  headerSearchIcon: {
    marginRight: 6,
  },
  headerSearchInput: {
    flex: 1,
    fontSize: 14,
    minWidth: 100,
    ...Platform.select({
      web: { outlineStyle: "none" as any },
    }),
  },
  headerClearButton: {
    padding: 2,
    marginLeft: 4,
  },
  count: {
    fontSize: 14,
    fontWeight: "600",
    opacity: 0.6,
  },
  statusPill: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  statusText: {
    fontSize: 10,
    fontWeight: "600",
    color: "white",
  },
  dateText: {
    fontSize: 12,
  },
  countText: {
    fontSize: 13,
    fontWeight: "600",
  },
  emptyState: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
    gap: 8,
  },
  emptyText: {
    fontSize: 16,
    fontWeight: "600",
    opacity: 0.6,
  },
});
